import { useEffect, useRef, useState } from 'react'

import { EMPTY_LIST, EMPTY_OBJECT } from '../../../../constants/common'
import { EMAIL_ENTITY } from '../../../../constants/entity'
import {
  useAsyncCallback,
  usePageBrowserTabTitle,
  usePageShowReload,
  useSelection,
} from '../../../../hooks'
import {
  useAsyncTable,
  useTableRefresh,
  useTableReloadAfterMassAction,
} from '../../../Table'
import { EMAIL_TABLE_ROW_ACTIONS_CONFIG } from '../../Email.constants'

export function useEmailTablePage({
  asyncTableOptions,
  entity = EMAIL_ENTITY,
  extraSelectionResetDeps = EMPTY_LIST,
  getPermanentFilters,
  pageTitle,
  sub = null,
  withBrowserTabTitle = true,
  onApplyTableConfig,
}) {
  const [isConfigOpen, setConfigOpen] = useState(false)

  const pageScrollWrapperRef = useRef(null)

  const asyncTable = useAsyncTable({
    ...asyncTableOptions,
    containerRef: pageScrollWrapperRef,
    entity,
    getPermanentFilters,
    sub,
  })

  const {
    columns,
    filterValues,
    handleReload,
    isInitiated,
    isRefetching,
    items,
    preview,
    view,
  } = asyncTable

  usePageBrowserTabTitle({
    isEnabled: withBrowserTabTitle,
    isLoading: !isInitiated,
    pageTitle,
  })

  const selection = useSelection({
    config: EMAIL_TABLE_ROW_ACTIONS_CONFIG,
    data: items,
    pagination: asyncTable.pagination,
  })

  const { handleCleanSelection } = selection

  const {
    handleChangeShouldReloadFilters,
    handleReloadTableAfterMassAction,
    shouldReloadFilters,
  } = useTableReloadAfterMassAction({
    filterValues,
    getPermanentFilters,
    sub,
    onReload: handleReload,
  })

  const { refreshButtonProps } = useTableRefresh({
    isRefetching,
    onReload: handleReload,
  })

  usePageShowReload(handleReload)

  useEffect(() => {
    handleCleanSelection()
  }, [...extraSelectionResetDeps, filterValues])

  const handleApply = useAsyncCallback(
    (obj = EMPTY_OBJECT) => {
      if (!onApplyTableConfig) {
        return Promise.resolve()
      }

      return onApplyTableConfig({
        columns,
        preview,
        view,
        ...obj,
      })
    },
    [columns, onApplyTableConfig, preview, view]
  )

  function handleConfigOpen() {
    setConfigOpen(true)
  }

  return {
    ...asyncTable,
    ...selection,
    entity,
    handleApply,
    handleChangeShouldReloadFilters,
    handleConfigOpen,
    handleReloadTableAfterMassAction,
    isConfigOpen,
    pageScrollWrapperRef,
    refreshButtonProps,
    setConfigOpen,
    shouldReloadFilters,
  }
}
